import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import AIManager from "@/pages/AIManager";
import Finance from "@/pages/Finance";
import Inventory from "@/pages/Inventory";
import InventoryCount from "@/pages/InventoryCount";
import Suppliers from "@/pages/Suppliers";
import Orders from "@/pages/Orders";
import Recipes from "@/pages/Recipes";
import Menu from "@/pages/Menu";
import Tasks from "@/pages/Tasks";
import Employees from "@/pages/Employees";
import Shifts from "@/pages/Shifts";
import Sales from "@/pages/Sales";
import ProductPerformance from "@/pages/ProductPerformance";
import DailyClose from "@/pages/DailyClose";
import DataQuality from "@/pages/DataQuality";
import Reports from "@/pages/Reports";
import Simulations from "@/pages/Simulations";
import Marketing from "@/pages/Marketing";
import Haccp from "@/pages/Haccp";
import Crm from "@/pages/Crm";
import Forecast from "@/pages/Forecast";
import Competitors from "@/pages/Competitors";
import MenuI18n from "@/pages/MenuI18n";
import PublicMenu from "@/pages/PublicMenu";
import Admin from "@/pages/Admin";
import Audit from "@/pages/Audit";
import Reconciliation from "@/pages/Reconciliation";
import Documents from "@/pages/Documents";
import MenuEngineering from "@/pages/MenuEngineering";
import EconomicConfig from "@/pages/EconomicConfig";
import SmartReorder from "@/pages/SmartReorder";
import StaffingForecast from "@/pages/StaffingForecast";

/**
 * FASE 7.4 STEP E — Shell wraps a page in ProtectedRoute + Layout.
 * `permission` and `anyPermissions` drive the RBAC guard. If neither is
 * given, only authentication is required (fallback for public/auth-only
 * pages like the Dashboard root and legacy sub-pages).
 * FASE 7.4 STEP F.1 — legacy `adminOnly` (role-hardcoded) prop removed.
 */
function Shell({ children, permission, anyPermissions }) {
  return (
    <ProtectedRoute permission={permission} anyPermissions={anyPermissions}>
      <Layout>{children}</Layout>
    </ProtectedRoute>
  );
}

function App() {
  useEffect(() => { document.title = "Carasau Café — Gestionale"; }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/m/:lang" element={<PublicMenu />} />

          {/* Dashboard — dashboard.view_basic (all 6 roles). */}
          <Route path="/"                    element={<Shell permission="dashboard.view_basic"><Dashboard /></Shell>} />

          {/* AI Manager — ai_manager.use (admin+manager). */}
          <Route path="/ai-manager"          element={<Shell permission="ai_manager.use"><AIManager /></Shell>} />

          {/* Finance & Reconciliation. */}
          <Route path="/finance"             element={<Shell permission="finance.read"><Finance /></Shell>} />
          <Route path="/reconciliation"      element={<Shell permission="bank.reconcile"><Reconciliation /></Shell>} />

          {/* Documents — read.all OR read.haccp_only (§31 ANY OF). */}
          <Route path="/documents"           element={<Shell anyPermissions={["documents.read.all", "documents.read.haccp_only"]}><Documents /></Shell>} />

          {/* Inventory / Orders / Suppliers. */}
          <Route path="/inventory"           element={<Shell permission="inventory.read"><Inventory /></Shell>} />
          <Route path="/inventory-count"     element={<Shell permission="inventory.count"><InventoryCount /></Shell>} />
          <Route path="/suppliers"           element={<Shell permission="inventory.read"><Suppliers /></Shell>} />
          <Route path="/orders"              element={<Shell permission="orders.read"><Orders /></Shell>} />

          {/* Recipes / Menu. */}
          <Route path="/recipes"             element={<Shell permission="recipes.read"><Recipes /></Shell>} />
          <Route path="/menu"                element={<Shell permission="menu.read"><Menu /></Shell>} />
          <Route path="/menu-i18n"           element={<Shell permission="menu.translate"><MenuI18n /></Shell>} />
          <Route path="/menu-engineering"    element={<Shell permission="dashboard.view_financial"><MenuEngineering /></Shell>} />

          {/* Ops & staff. */}
          <Route path="/tasks"               element={<Shell permission="tasks.read"><Tasks /></Shell>} />
          <Route path="/employees"           element={<Shell permission="employees.read_basic"><Employees /></Shell>} />
          <Route path="/shifts"              element={<Shell permission="shifts.read"><Shifts /></Shell>} />
          <Route path="/staffing"            element={<Shell permission="dashboard.view_financial"><StaffingForecast /></Shell>} />

          {/* Sales / Product tracking / Daily Close. */}
          <Route path="/sales"               element={<Shell permission="sales.read"><Sales /></Shell>} />
          <Route path="/product-performance" element={<Shell permission="sales.read"><ProductPerformance /></Shell>} />
          <Route path="/daily-close"         element={<Shell permission="daily_close.read"><DailyClose /></Shell>} />

          {/* Analytics & DQ. */}
          <Route path="/data-quality"        element={<Shell permission="data_quality.view"><DataQuality /></Shell>} />
          <Route path="/forecast"            element={<Shell permission="forecast.view"><Forecast /></Shell>} />
          <Route path="/reports"             element={<Shell permission="reports.export"><Reports /></Shell>} />
          <Route path="/simulations"         element={<Shell permission="simulations.run"><Simulations /></Shell>} />

          {/* CRM / Marketing / Competitors. */}
          <Route path="/crm"                 element={<Shell permission="crm.read"><Crm /></Shell>} />
          <Route path="/marketing"           element={<Shell permission="marketing.write"><Marketing /></Shell>} />
          <Route path="/competitors"         element={<Shell permission="marketing.write"><Competitors /></Shell>} />

          {/* HACCP. */}
          <Route path="/haccp"               element={<Shell permission="haccp.execute"><Haccp /></Shell>} />

          {/* Reorder — depends on orders.write (planning). */}
          <Route path="/reorder"             element={<Shell permission="orders.write"><SmartReorder /></Shell>} />

          {/* Admin. */}
          <Route path="/admin"               element={<Shell permission="users.read"><Admin /></Shell>} />
          <Route path="/audit"               element={<Shell permission="audit.read"><Audit /></Shell>} />
          <Route path="/economic-config"     element={<Shell permission="settings.write.financial"><EconomicConfig /></Shell>} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
